import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { FaCookieBite } from 'react-icons/fa'
import { IoClose, IoShieldCheckmarkOutline } from 'react-icons/io5'
import { Link } from 'react-router-dom'

const STORAGE_KEY = 'gid_cookie_consent'

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY)
    if (!consent) {
      // Pequeno delay para animação suave após carregar a página
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAcceptAll = () => {
    localStorage.setItem(STORAGE_KEY, 'all')
    setIsVisible(false)
  }

  const handleAcceptEssential = () => {
    localStorage.setItem(STORAGE_KEY, 'essential')
    setIsVisible(false)
  }

  const handleClose = () => {
    // Se fechar sem escolher, grava essenciais como padrão de segurança
    localStorage.setItem(STORAGE_KEY, 'essential')
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <ConsentContainer>
      <ConsentContent>
        <IconWrapper>
          <FaCookieBite size={24} color='#a6c10b' />
        </IconWrapper>

        <TextWrapper>
          <TitleRow>
            <Title>Privacidade e Uso de Cookies</Title>
            <SecurityBadge>
              <IoShieldCheckmarkOutline size={13} />
              <span>Ambiente Seguro</span>
            </SecurityBadge>
          </TitleRow>

          <Description>
            Utilizamos cookies essenciais para garantir o funcionamento seguro do sistema e cookies analíticos para
            aprimorar a sua experiência e desempenho na plataforma GID.{' '}
            <StyledLink to='/termos-de-uso'>
              Saiba mais em nossos Termos de Uso e Privacidade
            </StyledLink>
            .
          </Description>
        </TextWrapper>

        <ActionsWrapper>
          <SecondaryButton type='button' onClick={handleAcceptEssential}>
            Apenas Essenciais
          </SecondaryButton>
          <PrimaryButton type='button' onClick={handleAcceptAll}>
            Aceitar Todos
          </PrimaryButton>
        </ActionsWrapper>

        <CloseButton type='button' onClick={handleClose} title='Fechar aviso de cookies'>
          <IoClose size={18} />
        </CloseButton>
      </ConsentContent>
    </ConsentContainer>
  )
}

// --- ANIMAÇÕES E ESTILOS ---

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 30px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`

const ConsentContainer = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translate(-50%, 0);
  width: calc(100% - 48px);
  max-width: 860px;
  z-index: 99999999;
  animation: ${slideUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`

const ConsentContent = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 16px 22px;
  background: rgba(0, 20, 55, 0.95);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(166, 193, 11, 0.35);
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45), 0 0 20px rgba(166, 193, 11, 0.1);
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 18px 16px;
    gap: 14px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(166, 193, 11, 0.15);
  border: 1px solid rgba(166, 193, 11, 0.3);
  flex-shrink: 0;
`

const TextWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const Title = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  letter-spacing: 0.3px;
`

const SecurityBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 227, 150, 0.12);
  color: #00e396;
  border: 1px solid rgba(0, 227, 150, 0.25);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
`

const Description = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: #c4d1db;
  margin: 0;
  font-family: 'Montserrat', sans-serif;
`

const StyledLink = styled(Link)`
  color: #a6c10b;
  text-decoration: underline;
  font-weight: 600;
  transition: color 0.2s;

  &:hover {
    color: #c4e01b;
  }
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`

const SecondaryButton = styled.button`
  padding: 9px 16px;
  background: rgba(255, 255, 255, 0.06);
  color: #e0e0e0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.3);
  }

  @media (max-width: 768px) {
    flex: 1;
    text-align: center;
  }
`

const PrimaryButton = styled.button`
  padding: 9px 20px;
  background: #a6c10b;
  color: #001d53;
  border: none;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba(166, 193, 11, 0.35);
  transition: all 0.2s ease;

  &:hover {
    background: #b8d414;
    box-shadow: 0 6px 18px rgba(166, 193, 11, 0.5);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    flex: 1;
    text-align: center;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: #8fa0b3;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    top: 12px;
    right: 12px;
  }
`
